import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useSession } from 'next-auth/react'

import { extractTokenFromClientSession } from 'modules/customer'
import {
  DeliverySlot,
  getFirstAvailableDeliverySlot,
  getFirstAvailableTmsDeliverySlot,
  PostalCodeTypes,
  TMSTimeSlot,
  useDeliverySupport
} from 'modules/delivery'
import { IS_TMS_DELIVERY } from 'shared/config'

import { useDisplayCart } from '../../../store/cartStore'
import { useSyncSelectedDeliveryFromCart } from '../hooks/useSyncSelectedDeliveryFromCart'

export interface FirstAvailableDeliverySlots {
  firstAvailableDeliverySlot: DeliverySlot | null
  firstAvailableTmsDeliverySlot: TMSTimeSlot | null | undefined
}

export const FirstAvailableDeliverySlotsContext =
  createContext<FirstAvailableDeliverySlots | null>(null)

// Cart side of the delivery area: keeps the area in sync with the cart and
// fetches the first available slot for it. Must be mounted inside
// `DeliverySupportContextProvider`.
export const DeliverySelectionProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const session = useSession()
  const cart = useDisplayCart()
  const { postalCodeAndCityContext } = useDeliverySupport()

  const token = useMemo(() => extractTokenFromClientSession(session), [session])

  const [firstAvailableDeliverySlot, setFirstAvailableDeliverySlot] =
    useState<DeliverySlot | null>(null)
  const [firstAvailableTmsDeliverySlot, setFirstAvailableTmsDeliverySlot] =
    useState<TMSTimeSlot | null>(null)

  useSyncSelectedDeliveryFromCart()

  const fetchFirstAvailableDeliverySlot = useCallback(
    async (deliveryMethods: PostalCodeTypes[]) => {
      try {
        if (!token) return null

        const timeSlots = await Promise.all(
          deliveryMethods.map(async deliveryMethod => {
            try {
              const timeSlot = await getFirstAvailableDeliverySlot(
                deliveryMethod.id,
                token
              )

              return timeSlot
            } catch (err) {
              console.error(err)

              return null
            }
          })
        )

        const slots = timeSlots.filter(slot => slot !== null)

        setFirstAvailableDeliverySlot(slots[0] || null)

        return slots
      } catch (err) {
        console.error(err)

        return null
      }
    },
    [token]
  )

  const fetchFirstAvailableTmsDeliverySlot = useCallback(
    async (cartId?: string) => {
      if (!cartId) return
      try {
        const result = await getFirstAvailableTmsDeliverySlot(
          cartId,
          token || ''
        )

        setFirstAvailableTmsDeliverySlot(result)
      } catch (err) {
        console.error(err)
        setFirstAvailableTmsDeliverySlot(null)
      }
    },
    [token]
  )

  useEffect(() => {
    const fetchFirstAvailableSlot = async () => {
      if (session.status === 'loading' || !cart?.id) return

      if (
        !IS_TMS_DELIVERY &&
        postalCodeAndCityContext.isSupported &&
        postalCodeAndCityContext.deliveryMethods.length > 0
      ) {
        await fetchFirstAvailableDeliverySlot(
          postalCodeAndCityContext.deliveryMethods
        )
      } else if (
        IS_TMS_DELIVERY &&
        cart?.id &&
        session.status === 'authenticated' &&
        postalCodeAndCityContext.isSupported
      ) {
        await fetchFirstAvailableTmsDeliverySlot(cart.id)
      }
    }

    void fetchFirstAvailableSlot()
  }, [
    postalCodeAndCityContext,
    postalCodeAndCityContext.isSupported,
    postalCodeAndCityContext.deliveryMethods.length,
    cart?.id,
    session.status,
    postalCodeAndCityContext.deliveryMethods,
    fetchFirstAvailableDeliverySlot,
    fetchFirstAvailableTmsDeliverySlot
  ])

  return (
    <FirstAvailableDeliverySlotsContext.Provider
      value={{ firstAvailableDeliverySlot, firstAvailableTmsDeliverySlot }}
    >
      {children}
    </FirstAvailableDeliverySlotsContext.Provider>
  )
}
